import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Timer as TimerIcon } from '@mui/icons-material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import { AdminService } from '../../services/adminService';

/**
 * Durées des sessions de jeu. Aucune session n'est enregistrée : le serveur les
 * reconstitue à partir des parties terminées (`admin.queries` → `SESSIONS_CTE`),
 * une pause de plus de N minutes ouvrant une nouvelle session. Cet écran ne
 * recalcule rien.
 */

const formatMin = (v) => (v === null || v === undefined ? '—' : `${String(v).replace('.', ',')} min`);
const formatSemaine = (iso) =>
  iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : '';

function Chiffre({ titre, valeur, detail }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">{titre}</Typography>
        <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>{valeur}</Typography>
        {detail && <Typography variant="body2" color="text.secondary">{detail}</Typography>}
      </CardContent>
    </Card>
  );
}

export default function SessionsJeuSection({ segment = 'familles' }) {
  const [jours, setJours] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let annule = false;
    setLoading(true);
    AdminService.getPlaySessions(jours, 30, segment)
      .then((d) => { if (!annule) { setData(d); setError(null); } })
      .catch((err) => {
        console.error('Error fetching play sessions:', err);
        if (!annule) setError('Impossible de charger les sessions de jeu');
      })
      .finally(() => { if (!annule) setLoading(false); });
    return () => { annule = true; };
  }, [jours, segment]);

  const s = data?.synthese;
  const sessionsParJoueur = s?.joueurs ? Math.round((s.sessions / s.joueurs) * 10) / 10 : null;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          Durée des sessions de jeu
        </Typography>
        <ToggleButtonGroup value={jours} exclusive size="small" onChange={(e, v) => { if (v) setJours(v); }}>
          <ToggleButton value={30}>30 j</ToggleButton>
          <ToggleButton value={90}>90 j</ToggleButton>
          <ToggleButton value={365}>1 an</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Session = suite de parties d'un même joueur sans pause de plus de{' '}
        {data?.pauseMinutes ?? 30} min. Durée = du début estimé de la première partie à la fin de
        la dernière. ⚠️ Estimation basse : la durée d'une partie est la somme des temps de réponse
        (plafonnée à {data?.partieMaxMinutes ?? 30} min) — lecture des leçons, animations et temps
        avant la première réponse ne sont pas comptés.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} lg={3}>
              <Chiffre
                titre="Durée médiane"
                valeur={formatMin(s?.medianeMin)}
                detail={`Moyenne ${formatMin(s?.moyenneMin)}`}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <Chiffre
                titre="Dispersion"
                valeur={`${formatMin(s?.p25Min)} – ${formatMin(s?.p75Min)}`}
                detail={`Quartiles 25 / 75 % · 90 % : ${formatMin(s?.p90Min)}`}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <Chiffre
                titre="Sessions"
                valeur={s?.sessions ?? 0}
                detail={`${s?.joueurs ?? 0} joueurs · ${sessionsParJoueur ?? '—'} session(s) par joueur`}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <Chiffre
                titre="Parties par session"
                valeur={s?.partiesMediane === null || s?.partiesMediane === undefined ? '—' : String(s.partiesMediane).replace('.', ',')}
                detail="Médiane"
              />
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Répartition des durées" avatar={<TimerIcon color="primary" />} />
                <CardContent>
                  <Box sx={{ height: 300, width: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data?.distribution ?? []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="tranche" tick={{ fontSize: 11 }} />
                        <YAxis allowDecimals={false} />
                        <RechartsTooltip formatter={(v) => [v, 'Sessions']} />
                        <Bar dataKey="sessions" fill="#2196f3" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Durée par semaine" avatar={<TimerIcon color="secondary" />} />
                <CardContent>
                  <Box sx={{ height: 300, width: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={data?.semaines ?? []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="semaine" tick={{ fontSize: 11 }} tickFormatter={formatSemaine} />
                        <YAxis unit=" min" />
                        <RechartsTooltip
                          labelFormatter={(l) => `Semaine du ${formatSemaine(l)}`}
                          formatter={(v, name, item) => [
                            `${String(v).replace('.', ',')} min`,
                            `${name} (${item.payload.sessions} sessions)`,
                          ]}
                        />
                        <Legend />
                        <Line type="monotone" dataKey="medianeMin" name="Médiane" stroke="#2196f3" strokeWidth={2} />
                        <Line type="monotone" dataKey="moyenneMin" name="Moyenne" stroke="#ff9800" strokeWidth={2} strokeDasharray="5 5" />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}
